import { useRef, useState } from "react";

/** Immutable move, shared by the drag and the move-up/down buttons. */
export const moveItem = (items, from, to) => {
  if (from === to) return items;
  if (from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Card reordering on native HTML5 drag events — no extra dependency.
 *
 * The list is rearranged as the pointer crosses each card, so the grid shows
 * the outcome during the drag instead of after it. `onCommit` fires once, on
 * drop, with the final order; a cancelled drag (Escape, or releasing outside a
 * card) restores the order captured at drag start.
 *
 * Dragging is also the only affordance a mouse gives, so `moveBy` exposes the
 * same reordering to the keyboard-reachable buttons on each card.
 *
 * Both the dragged index and the current order are held in refs as well as
 * state: `dragenter` can fire several times before React re-renders, and a
 * handler reading either from its render closure would then work off a stale
 * value and move the wrong card.
 */
export function useDragReorder({ items, setItems, onCommit, disabled = false }) {
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const snapshot = useRef(null);
  const dropped = useRef(false);

  const latest = useRef(items);
  latest.current = items;

  const setDrag = (index) => {
    dragIndexRef.current = index;
    setDragIndex(index);
  };

  const handleDragStart = (index) => (event) => {
    snapshot.current = latest.current;
    dropped.current = false;
    setDrag(index);
    event.dataTransfer.effectAllowed = "move";
    // Firefox will not start a drag without payload on the transfer.
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragEnter = (index) => () => {
    const from = dragIndexRef.current;
    if (from === null || from === index) return;
    const next = moveItem(latest.current, from, index);
    latest.current = next;
    setItems(next);
    setDrag(index);
  };

  const handleDragOver = (event) => {
    if (dragIndexRef.current === null) return;
    // Without this the browser rejects the drop and no drop event fires.
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event) => {
    if (dragIndexRef.current === null) return;
    event.preventDefault();
    dropped.current = true;
    onCommit(latest.current);
  };

  const handleDragEnd = () => {
    if (!dropped.current && snapshot.current) {
      latest.current = snapshot.current;
      setItems(snapshot.current);
    }
    snapshot.current = null;
    dropped.current = false;
    setDrag(null);
  };

  const moveBy = (index, offset) => {
    const next = moveItem(latest.current, index, index + offset);
    if (next === latest.current) return;
    latest.current = next;
    setItems(next);
    onCommit(next);
  };

  const getCardProps = (index) =>
    disabled
      ? {}
      : {
          draggable: true,
          onDragStart: handleDragStart(index),
          onDragEnter: handleDragEnter(index),
          onDragOver: handleDragOver,
          onDrop: handleDrop,
          onDragEnd: handleDragEnd,
        };

  return {
    dragIndex,
    isDragging: dragIndex !== null,
    getCardProps,
    moveBy: disabled ? () => {} : moveBy,
  };
}

export default useDragReorder;
